import { useEffect, useState, useMemo, ReactElement } from 'react';
import {
  Box,
  CircularProgress,
  LinearProgress,
  Card,
  CardContent,
  Typography,
  Button,
  ButtonGroup,
  Menu,
  MenuItem,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Tooltip,
  Snackbar,
  Alert,
  Chip as _Chip,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Description as DocumentIcon,
  Download as DownloadIcon,
  AutoFixHigh as AutoFixHighIcon,
  Warning as WarningIcon,
  Folder as FolderIcon,
  FolderOpen as FolderOpenIcon,
  ArrowDropDown as ArrowDropDownIcon,
  DriveFileMove as _DriveFileMoveIcon,
  CheckCircle as _CheckCircleIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import api from '../api/axios';

interface GeneratedDocument {
  id: string;
  filename: string;
  storage_key: string;
  size_bytes: number;
  created_at: string;
  document_group?: string;
  scheme_template_id?: string;
  // SharePoint sync fields
  ms_item_id?: string;
  ms_web_url?: string;
  synced_at?: string;
  // Office protocol URL for opening in desktop app (ms-word:ofe|u|...)
  url?: string;
}

interface CreatedByUser {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
}

interface DocumentGeneration {
  id: string;
  folder: string;
  config_json: any;
  created_at: string;
  created_by: CreatedByUser | null;
  documents: GeneratedDocument[];
}

interface TemplateDocumentsProps {
  caseId: string;
  caseData: any;
  onDocumentMove?: () => void;
  // Review mode: simplified title ("Documents") and hides "Last Generated" timestamp
  reviewMode?: boolean;
}

export default function TemplateDocuments({ caseId, caseData, onDocumentMove, reviewMode = false }: TemplateDocumentsProps) {
  const { access } = useAuth();
  const headers = useMemo(() => ({ Authorization: `Bearer ${access}` }), [access]);

  const [generations, setGenerations] = useState<DocumentGeneration[]>([]);
  const [loading, setLoading] = useState(true);
  const [generatingDocs, setGeneratingDocs] = useState(false);
  const [_taskProgress, setTaskProgress] = useState<number>(0);
  const [taskStatus, setTaskStatus] = useState<string>('');
  const [scopingData, setScopingData] = useState<any>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [confirmDocType, setConfirmDocType] = useState<string>('all');
  const [currentDocType, setCurrentDocType] = useState<string>('all');
  const [expandedFolders, setExpandedFolders] = useState<{ [key: string]: boolean }>({});
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);
  const [movingDoc, setMovingDoc] = useState<string | null>(null);
  const [_movingFolder, setMovingFolder] = useState<string | null>(null);
  const [_movingProgress, setMovingProgress] = useState<{ current: number; total: number } | null>(null);
  const [caseFilesNames, setCaseFilesNames] = useState<Set<string>>(new Set());
  const [downloadingZip, setDownloadingZip] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' | 'warning' }>({
    open: false,
    message: '',
    severity: 'success'
  });

  useEffect(() => {
    fetchData();
  }, [caseId, access]);

  const fetchData = async () => {
    if (!caseId) return;

    try {
      setLoading(true);
      const [generationsRes, scopingRes, caseDocsRes] = await Promise.all([
        api.get(`/docgen/${caseId}/document_generations/`, { headers }),
        api.get(`/snfs/scoping/?case_id=${caseId}`, { headers }),
        api.get(`/cases/${caseId}/documents/`, { headers })
      ]);

      setGenerations(generationsRes.data || []);
      setScopingData(scopingRes.data);

      // Build set of filenames that already exist in case files
      const existingNames = new Set<string>(
        (caseDocsRes.data || []).map((doc: any) => doc.name || doc.filename).filter(Boolean)
      );
      setCaseFilesNames(existingNames);
    } catch (err) {
      console.error('Failed to fetch template documents:', err);
    } finally {
      setLoading(false);
    }
  };

  // Build ownership dictionary from scoping data
  const buildOwnershipDict = (owners: any[]) => {
    const dict: any = {};
    owners?.forEach(owner => {
      if (owner.cms_owner_id) {
        dict[owner.cms_owner_id] = {
          name: owner.name,
          type: owner.owner_type,
          role: owner.role,
          address: {
            line1: owner.address_line_1,
            city: owner.city,
            state: owner.state,
            zip: owner.zip_code
          },
          facilities_count: owner.num_facilities_in_case
        };
      }
    });
    return dict;
  };

  // Get default status message based on document type
  const getDefaultStatusMessage = (docType: string) => {
    switch (docType) {
      case 'complaint':
        return 'Generating complaint...';
      case 'exhibits':
        return 'Generating exhibits...';
      case 'attachments':
        return 'Generating attachments...';
      case 'review_report':
        return 'Generating review report...';
      default:
        return 'Generating documents...';
    }
  };

  // Poll for task status
  const pollTaskStatus = async (taskId: string) => {
    const pollInterval = setInterval(async () => {
      try {
        const { data } = await api.get(`/celery-tasks/${taskId}/`, { headers });

        setTaskProgress(data.progress || 0);
        setTaskStatus(data.status_message || getDefaultStatusMessage(currentDocType));

        if (data.state === 'SUCCESS') {
          clearInterval(pollInterval);
          setGeneratingDocs(false);
          // Refresh documents
          await fetchData();
        } else if (data.state === 'FAILURE') {
          clearInterval(pollInterval);
          setGeneratingDocs(false);
          console.error('Document generation failed');
        }
      } catch (err) {
        console.error('Failed to poll task status:', err);
        clearInterval(pollInterval);
        setGeneratingDocs(false);
      }
    }, 2000);
  };

  // Handle menu open
  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  // Handle menu close
  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  // Handle document type selection from menu
  const handleGenerateDocType = (docType: string) => {
    handleMenuClose();

    // Check if documents already exist
    if (generations.length > 0) {
      // Show confirmation dialog first
      setConfirmDocType(docType);
      setShowConfirmDialog(true);
    } else {
      // No existing documents, generate directly
      generateDocuments(docType);
    }
  };

  // Generate documents
  const generateDocuments = async (docType: string = 'all') => {
    if (!caseData || !scopingData) return;

    setGeneratingDocs(true);
    setCurrentDocType(docType);
    setTaskProgress(0);
    setTaskStatus('');

    try {
      // Build config from scoping data
      const config = {
        scheme_ccns: scopingData.facilities?.map((f: any) => f.ccn) || [],
        all_ccns: scopingData.facilities?.map((f: any) => f.ccn) || [],
        court: caseData.court,
        lead_defendant: scopingData.owners?.find((o: any) => o.role === 'LEAD')?.name
                        || caseData.lead_defendant
                        || caseData.name,
        ownership_dict: buildOwnershipDict(scopingData.owners),
        total_billed: scopingData.summary?.total_billed || 0,
        total_paid: scopingData.summary?.total_paid || 0,
        override_map: {},
        include_dba: false,
        force_regenerate: generations.length > 0, // Always true if documents exist
        document_type: docType,
        file_month: "2023-06", // Default file month for review report
      };

      // Use different endpoints for special document types
      let endpoint: string;
      if (docType === 'review_report') {
        endpoint = `/docgen/${caseId}/review_report/`;
      } else if (docType === 'internal_cms_data_request') {
        endpoint = `/docgen/${caseId}/internal_cms_data_request/`;
      } else if (docType === 'proposed_cids_with_beneficiaries') {
        endpoint = `/docgen/${caseId}/proposed_cids_with_beneficiaries/`;
      } else {
        endpoint = `/docgen/${caseId}/generate_docs/`;
      }

      const response = await api.post(endpoint, config, { headers });

      // Start polling for progress
      const taskId = response.data.task_id;
      pollTaskStatus(taskId);
    } catch (err: any) {
      console.error('Failed to generate documents:', err);
      setGeneratingDocs(false);
    }
  };

  // Handle confirmed generation
  const handleConfirmRegenerate = async () => {
    setShowConfirmDialog(false);
    await generateDocuments(confirmDocType);
  };

  // Handle cancel generation
  const handleCancelRegenerate = () => {
    setShowConfirmDialog(false);
    setCurrentDocType('all'); // Reset to default
  };

  // Build nested folder structure from documents
  interface FolderNode {
    name: string;
    path: string;
    files: GeneratedDocument[];
    subfolders: { [key: string]: FolderNode };
  }

  const buildFolderTree = (documents: GeneratedDocument[]) => {
    const ungrouped: GeneratedDocument[] = [];
    const rootFolders: { [key: string]: FolderNode } = {};

    documents.forEach(doc => {
      if (!doc.document_group) {
        ungrouped.push(doc);
      } else {
        // Split by "/" to handle nested paths
        const parts = doc.document_group.split('/');
        let currentLevel = rootFolders;

        parts.forEach((part, index) => {
          const isLastPart = index === parts.length - 1;
          const fullPath = parts.slice(0, index + 1).join('/');

          if (!currentLevel[part]) {
            currentLevel[part] = {
              name: part.replace(/_/g, ' '),  // Display with spaces instead of underscores
              path: fullPath,
              files: [],
              subfolders: {}
            };
          }

          if (isLastPart) {
            // Add document to the leaf folder
            currentLevel[part].files.push(doc);
          } else {
            // Navigate to subfolder
            currentLevel = currentLevel[part].subfolders;
          }
        });
      }
    });

    // Sort ungrouped documents by filename
    ungrouped.sort((a, b) =>
      a.filename.localeCompare(b.filename, undefined, { numeric: true, sensitivity: 'base' })
    );

    // Sort files within each folder
    const sortFolderFiles = (folder: FolderNode) => {
      folder.files.sort((a, b) =>
        a.filename.localeCompare(b.filename, undefined, { numeric: true, sensitivity: 'base' })
      );
      Object.values(folder.subfolders).forEach(sortFolderFiles);
    };

    Object.values(rootFolders).forEach(sortFolderFiles);

    return { ungrouped, rootFolders };
  };

  // Toggle folder expansion
  const toggleFolder = (folderKey: string) => {
    setExpandedFolders(prev => ({
      ...prev,
      [folderKey]: !prev[folderKey]
    }));
  };

  // Download single document
  const handleDownloadDocument = async (doc: GeneratedDocument) => {
    try {
      const response = await api.get(
        `/docgen/${caseId}/presign/?key=${encodeURIComponent(doc.storage_key)}`,
        { headers }
      );

      const downloadUrl = response.data.url;
      const isLocalUrl = downloadUrl.includes('localhost') || downloadUrl.includes('127.0.0.1');

      if (isLocalUrl) {
        const urlObj = new URL(downloadUrl);
        let relativePath = urlObj.pathname + urlObj.search;

        if (relativePath.startsWith('/api/')) {
          relativePath = relativePath.substring(5);
        }

        const fileResponse = await api.get(relativePath, {
          headers,
          responseType: 'blob'
        });

        const blob = new Blob([fileResponse.data]);
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', doc.filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } else {
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.setAttribute('download', doc.filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
      }
    } catch (err) {
      console.error('Failed to download document:', err);
    }
  };

  // Download all documents from current generation as zip
  const handleDownloadAll = async (generationId: string) => {
    setDownloadingZip(true);
    try {
      const response = await api.get(
        `/docgen/${caseId}/download_generation_zip/?generation_id=${generationId}`,
        {
          headers,
          responseType: 'blob'
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `template_documents_${generationId.substring(0, 8)}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      setSnackbar({
        open: true,
        message: 'Download complete!',
        severity: 'success'
      });
    } catch (err) {
      console.error('Failed to download all documents:', err);
      setSnackbar({
        open: true,
        message: 'Failed to download documents as zip',
        severity: 'error'
      });
    } finally {
      setDownloadingZip(false);
    }
  };

  // Move document to Case Documents
  const _handleMoveDocument = async (doc: GeneratedDocument) => {
    if (!doc.id) return;

    // Check if already exists in Case Documents
    if (caseFilesNames.has(doc.filename)) {
      setSnackbar({
        open: true,
        message: `"${doc.filename}" already exists in Case Documents`,
        severity: 'info'
      });
      return;
    }

    setMovingDoc(doc.id);
    try {
      await api.post(
        `/cases/${caseId}/documents/copy_from_template/`,
        {
          generated_document_id: doc.id,
          storage_key: doc.storage_key,
          filename: doc.filename
        },
        { headers }
      );

      // Update the set of case documents names
      setCaseFilesNames(prev => new Set([...Array.from(prev), doc.filename]));

      // Show success message
      setSnackbar({
        open: true,
        message: `Successfully moved "${doc.filename}" to Case Documents`,
        severity: 'success'
      });

      // Notify parent to refresh case documents
      if (onDocumentMove) {
        onDocumentMove();
      }
    } catch (err: any) {
      console.error('Failed to move document to Case Documents:', err);
      const errorMessage = err.response?.data?.detail || 'Failed to move document to Case Documents';
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    } finally {
      setMovingDoc(null);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Handle document click - open in local Office app if possible
  const handleDocumentClick = (doc: GeneratedDocument) => {
    const isOfficeUrl = (u: string) => u.startsWith('ms-word:') || u.startsWith('ms-excel:') || u.startsWith('ms-powerpoint:');

    if (doc.url && isOfficeUrl(doc.url)) {
      // Open with the protocol handler to launch the desktop app
      window.location.href = doc.url;
    } else if (doc.ms_web_url) {
      // Non-Office file synced to SharePoint - open in browser
      window.open(doc.ms_web_url, '_blank');
    } else {
      // Not synced - download from S3
      handleDownloadDocument(doc);
    }
  };

  // Recursively collect all documents from a folder and its subfolders
  const collectFolderDocuments = (folder: { files: GeneratedDocument[]; subfolders: { [key: string]: any } }): GeneratedDocument[] => {
    let allDocs: GeneratedDocument[] = [...folder.files];

    Object.values(folder.subfolders).forEach((subfolder: any) => {
      allDocs = allDocs.concat(collectFolderDocuments(subfolder));
    });

    return allDocs;
  };

  // Move all documents in a folder to Case Documents
  const _handleMoveFolderDocuments = async (folder: { name: string; path: string; files: GeneratedDocument[]; subfolders: { [key: string]: any } }) => {
    const allDocs = collectFolderDocuments(folder);
    const docsToMove = allDocs.filter(doc => !caseFilesNames.has(doc.filename));

    if (docsToMove.length === 0) {
      setSnackbar({
        open: true,
        message: `All documents in "${folder.name}" are already in Case Documents`,
        severity: 'info'
      });
      return;
    }

    setMovingFolder(folder.path);
    setMovingProgress({ current: 0, total: docsToMove.length });

    let movedCount = 0;
    let failedCount = 0;
    const newNames = new Set(Array.from(caseFilesNames));

    for (let i = 0; i < docsToMove.length; i++) {
      const doc = docsToMove[i];
      try {
        await api.post(
          `/cases/${caseId}/documents/copy_from_template/`,
          {
            generated_document_id: doc.id,
            storage_key: doc.storage_key,
            filename: doc.filename
          },
          { headers }
        );
        movedCount++;
        newNames.add(doc.filename);
      } catch (err) {
        console.error(`Failed to move ${doc.filename}:`, err);
        failedCount++;
      }
      setMovingProgress({ current: i + 1, total: docsToMove.length });
    }

    // Update the set of case documents names
    setCaseFilesNames(newNames);

    setMovingFolder(null);
    setMovingProgress(null);

    if (failedCount === 0) {
      setSnackbar({
        open: true,
        message: `Successfully moved ${movedCount} document${movedCount !== 1 ? 's' : ''} from "${folder.name}" to Case Documents`,
        severity: 'success'
      });
    } else {
      setSnackbar({
        open: true,
        message: `Moved ${movedCount} document(s), ${failedCount} failed`,
        severity: 'warning'
      });
    }

    // Notify parent to refresh case documents
    if (onDocumentMove) {
      onDocumentMove();
    }
  };

  // Show loading state while fetching scoping data
  if (loading || !scopingData) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        {/* Header with Generate Button */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
            {reviewMode ? '📁 Documents' : '📁 Template Documents'}
          </Typography>

          <Box>
            <ButtonGroup variant="contained" disabled={generatingDocs}>
              <Button
                color="primary"
                startIcon={<AutoFixHighIcon />}
                onClick={() => handleGenerateDocType('all')}
                size="medium"
              >
                Generate Documents
              </Button>
              <Button
                color="primary"
                size="small"
                onClick={handleMenuClick}
                aria-controls={menuOpen ? 'generate-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={menuOpen ? 'true' : undefined}
              >
                <ArrowDropDownIcon />
              </Button>
            </ButtonGroup>
            <Menu
              id="generate-menu"
              anchorEl={anchorEl}
              open={menuOpen}
              onClose={handleMenuClose}
              MenuListProps={{
                'aria-labelledby': 'generate-button',
              }}
            >
              <MenuItem onClick={() => handleGenerateDocType('all')}>
                <ListItemText primary="All Documents" secondary="Generate complete bundle" />
              </MenuItem>
              <MenuItem onClick={() => handleGenerateDocType('complaint')}>
                <ListItemText primary="Complaint Only" secondary="01 Complaint" />
              </MenuItem>
              <MenuItem onClick={() => handleGenerateDocType('exhibits')}>
                <ListItemText primary="Written Disclosure & Exhibits" secondary="02 Written Disclosure + Exhibits A, B, C" />
              </MenuItem>
              <MenuItem onClick={() => handleGenerateDocType('attachments')}>
                <ListItemText primary="Investigative Supplement & Attachments" secondary="03 Supplement + Attachments 1-5" />
              </MenuItem>
              <MenuItem onClick={() => handleGenerateDocType('review_report')}>
                <ListItemText primary="Review Report" secondary="Complaint (no defendant section), Facility List, Ownership Grid, Defendants Section" />
              </MenuItem>
              <MenuItem onClick={() => handleGenerateDocType('internal_cms_data_request')}>
                <ListItemText primary="Internal CMS Data Request" secondary="Document with list of CCNs and Organization Names" />
              </MenuItem>
              <MenuItem onClick={() => handleGenerateDocType('proposed_cids_with_beneficiaries')}>
                <ListItemText primary="Proposed CIDs with Beneficiaries" secondary="One document per facility with beneficiary table" />
              </MenuItem>
            </Menu>
          </Box>
        </Box>

        {/* Progress indicator during generation */}
        {generatingDocs && (
          <Box sx={{ py: 4, px: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <CircularProgress size={24} sx={{ mr: 2 }} />
              <Typography variant="body1" color="text.primary" sx={{ fontWeight: 500 }}>
                {taskStatus || getDefaultStatusMessage(currentDocType)}
              </Typography>
            </Box>
            <Box sx={{ width: '100%' }}>
              <LinearProgress
                variant="indeterminate"
                sx={{ height: 8, borderRadius: 1 }}
              />
            </Box>
          </Box>
        )}

        {/* Document Generations List */}
        {!generatingDocs && generations.length === 0 && (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <Typography variant="body1" color="text.secondary">
              No documents generated yet. Click 'Generate All Documents' to create the full bundle.
            </Typography>
          </Box>
        )}

        {!generatingDocs && generations.length > 0 && (() => {
          // Show only the most recent generation
          const latestGen = generations[0];
          const generatedDate = new Date(latestGen.created_at);
          const formattedDate = generatedDate.toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
          });
          const { ungrouped, rootFolders } = buildFolderTree(latestGen.documents);

          // Recursive function to count files in folder and all subfolders
          const countFilesRecursive = (folder: FolderNode): number => {
            let count = folder.files.length;
            Object.values(folder.subfolders).forEach(subfolder => {
              count += countFilesRecursive(subfolder);
            });
            return count;
          };

          // Extract attachment number from filename or folder name
          const extractAttachmentNumber = (name: string): number | null => {
            const match = name.match(/Attachment\s+(\d+)/i);
            return match ? parseInt(match[1], 10) : null;
          };

          // Recursive function to render folder and its contents
          const renderFolder = (folder: FolderNode, indentLevel: number = 0): ReactElement => {
            const folderKey = `${latestGen.id}-${folder.path}`;
            const isExpanded = expandedFolders[folderKey] || false;
            const totalFiles = countFilesRecursive(folder);

            return (
              <Box key={folder.path}>
                {/* Folder Header */}
                <ListItem
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    mb: 1,
                    bgcolor: 'grey.50',
                    '&:hover': {
                      bgcolor: 'grey.100',
                    },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', cursor: 'pointer' }} onClick={() => toggleFolder(folderKey)}>
                    <ListItemIcon>
                      {isExpanded ? (
                        <FolderOpenIcon color="primary" />
                      ) : (
                        <FolderIcon color="primary" />
                      )}
                    </ListItemIcon>
                    <ListItemText
                      primary={folder.name}
                      secondary={`${totalFiles} ${totalFiles === 1 ? 'file' : 'files'}`}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ExpandMoreIcon
                      sx={{
                        transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.3s',
                      }}
                    />
                  </Box>
                </ListItem>

                {/* Folder Contents - shown when expanded */}
                {isExpanded && (
                  <Box sx={{ pl: 4, mb: 1 }}>
                    {(() => {
                      // Combine files and subfolders into a single array for sorting
                      const filesWithType = folder.files.map(doc => ({
                        type: 'file' as const,
                        data: doc,
                        sortKey: extractAttachmentNumber(doc.filename) || doc.filename,
                      }));

                      const subfoldersWithType = Object.values(folder.subfolders).map(subfolder => ({
                        type: 'folder' as const,
                        data: subfolder,
                        sortKey: extractAttachmentNumber(subfolder.name) || subfolder.name,
                      }));

                      // Combine and sort by attachment number (if present) or by name
                      const combined = [...filesWithType, ...subfoldersWithType];
                      combined.sort((a, b) => {
                        if (typeof a.sortKey === 'number' && typeof b.sortKey === 'number') {
                          return a.sortKey - b.sortKey;
                        }
                        if (typeof a.sortKey === 'number') return -1;
                        if (typeof b.sortKey === 'number') return 1;
                        return String(a.sortKey).localeCompare(String(b.sortKey), undefined, {
                          numeric: true,
                          sensitivity: 'base'
                        });
                      });

                      // Render in sorted order
                      return combined.map((item, _index) => {
                        if (item.type === 'file') {
                          const doc = item.data;
                          const _isMoving = movingDoc === doc.id;
                          const _alreadyInCaseFiles = caseFilesNames.has(doc.filename);
                          return (
                            <ListItem
                              key={doc.id}
                              onClick={() => handleDocumentClick(doc)}
                              sx={{
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 1,
                                mb: 1,
                                cursor: 'pointer',
                                '&:hover': {
                                  bgcolor: 'action.hover',
                                },
                              }}
                              secondaryAction={
                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                  {doc.ms_web_url && (
                                    <Tooltip title="Open in SharePoint">
                                      <IconButton
                                        edge="end"
                                        onClick={(e) => { e.stopPropagation(); window.open(doc.ms_web_url, '_blank'); }}
                                        size="small"
                                        color="primary"
                                      >
                                        <OpenInNewIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                  <Tooltip title="Download">
                                    <IconButton
                                      edge="end"
                                      onClick={(e) => { e.stopPropagation(); handleDownloadDocument(doc); }}
                                      size="small"
                                    >
                                      <DownloadIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              }
                            >
                              <ListItemIcon>
                                <DocumentIcon color="primary" />
                              </ListItemIcon>
                              <ListItemText
                                primary={doc.filename}
                                secondary={`${(doc.size_bytes / 1024).toFixed(1)} KB${doc.synced_at ? ' • Synced to SharePoint' : ''}`}
                              />
                            </ListItem>
                          );
                        } else {
                          return renderFolder(item.data, indentLevel + 1);
                        }
                      });
                    })()}
                  </Box>
                )}
              </Box>
            );
          };

          return (
            <Box>
              {/* Last Generated Timestamp with Download All button - timestamp hidden in review mode */}
              {!reviewMode && (
                <Box sx={{ mb: 2, px: 2, py: 1.5, bgcolor: 'primary.50', borderRadius: 1, border: '1px solid', borderColor: 'primary.200', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Last Generated:</strong> {formattedDate}
                    {latestGen.created_by && (
                      <> by {latestGen.created_by.first_name} {latestGen.created_by.last_name}</>
                    )}
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={downloadingZip ? <CircularProgress size={16} /> : <DownloadIcon />}
                    onClick={() => handleDownloadAll(latestGen.id)}
                    disabled={downloadingZip}
                  >
                    {downloadingZip ? 'Downloading...' : 'Download All'}
                  </Button>
                </Box>
              )}


              <List dense>
                {(() => {
                  // Get sort priority for demo mode filenames (without 01, 02, 03 prefixes)
                  const getDemoSortPriority = (name: string): number => {
                    const lowerName = name.toLowerCase();
                    if (lowerName.startsWith('complaint')) return 1;
                    if (lowerName.startsWith('cms_internal') || lowerName.startsWith('cms internal')) return 2;
                    if (lowerName.startsWith('disclosure')) return 3;  // Disclosure_of_Material_Evidence folder
                    if (lowerName.startsWith('first_subpoena') || lowerName.startsWith('first subpoena')) return 4;
                    if (lowerName.startsWith('second_subpoena') || lowerName.startsWith('second subpoena')) return 5;
                    return 10; // Other items at the end
                  };

                  // Combine ungrouped files and root folders for proper sorting
                  const filesWithType = ungrouped.map(doc => ({
                    type: 'file' as const,
                    data: doc,
                    sortKey: doc.filename,
                  }));

                  const foldersWithType = Object.values(rootFolders).map(folder => ({
                    type: 'folder' as const,
                    data: folder,
                    sortKey: folder.name,
                  }));

                  // Combine and sort by name (with numeric sorting for 01, 02, 03 prefixes)
                  // Also handles demo mode filenames without prefixes using priority
                  const combined = [...filesWithType, ...foldersWithType];
                  combined.sort((a, b) => {
                    // Check if either item is a demo mode filename (no 01, 02, 03 prefix)
                    const aHasNumericPrefix = /^\d+\s/.test(a.sortKey);
                    const bHasNumericPrefix = /^\d+\s/.test(b.sortKey);

                    // If neither has numeric prefix, use demo sort priority
                    if (!aHasNumericPrefix && !bHasNumericPrefix) {
                      const aPriority = getDemoSortPriority(a.sortKey);
                      const bPriority = getDemoSortPriority(b.sortKey);
                      if (aPriority !== bPriority) {
                        return aPriority - bPriority;
                      }
                    }

                    return a.sortKey.localeCompare(b.sortKey, undefined, {
                      numeric: true,
                      sensitivity: 'base'
                    });
                  });

                  // Render in sorted order
                  return combined.map((item) => {
                    if (item.type === 'file') {
                      const doc = item.data;
                      const _isMoving = movingDoc === doc.id;
                      const _alreadyInCaseFiles = caseFilesNames.has(doc.filename);
                      return (
                        <ListItem
                          key={doc.id}
                          onClick={() => handleDocumentClick(doc)}
                          sx={{
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1,
                            mb: 1,
                            cursor: 'pointer',
                            '&:hover': {
                              bgcolor: 'action.hover',
                            },
                          }}
                          secondaryAction={
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                              {doc.ms_web_url && (
                                <Tooltip title="Open in SharePoint">
                                  <IconButton
                                    edge="end"
                                    onClick={(e) => { e.stopPropagation(); window.open(doc.ms_web_url, '_blank'); }}
                                    size="small"
                                    color="primary"
                                  >
                                    <OpenInNewIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                              <Tooltip title="Download">
                                <IconButton
                                  edge="end"
                                  onClick={(e) => { e.stopPropagation(); handleDownloadDocument(doc); }}
                                  size="small"
                                >
                                  <DownloadIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          }
                        >
                          <ListItemIcon>
                            <DocumentIcon color="primary" />
                          </ListItemIcon>
                          <ListItemText
                            primary={doc.filename}
                            secondary={`${(doc.size_bytes / 1024).toFixed(1)} KB${doc.synced_at ? ' • Synced to SharePoint' : ''}`}
                          />
                        </ListItem>
                      );
                    } else {
                      return renderFolder(item.data);
                    }
                  });
                })()}
              </List>
            </Box>
          );
        })()}
      </CardContent>

      {/* Confirmation Dialog */}
      <Dialog
        open={showConfirmDialog}
        onClose={handleCancelRegenerate}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningIcon color="warning" />
          Generate Documents?
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirmDocType === 'all'
              ? 'Current template documents will be deleted and replaced with a new complete bundle.'
              : `Current template documents will be deleted and replaced with new ${
                  confirmDocType === 'complaint' ? 'complaint' :
                  confirmDocType === 'exhibits' ? 'written disclosure and exhibits' :
                  'investigative supplement and attachments'
                } documents.`
            }
          </DialogContentText>
          <DialogContentText sx={{ mt: 2, fontWeight: 'bold' }}>
            Are you sure you want to continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelRegenerate} color="inherit">
            Cancel
          </Button>
          <Button onClick={handleConfirmRegenerate} variant="contained" color="warning">
            Yes, Generate
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Card>
  );
}
